<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import { useAnalysisStore } from '@/store/modules/analysis';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';

const { getGanjiCoverData } = fiveMixin();

const props = defineProps({
  isMobile: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    default: () => ({}),
  },
});

// 데이터 접근 편의를 위한 computed
const 사주정보 = computed(() => props.data?.사주정보 || {});

const store = useAnalysisStore();
const dataStore = useAnalysisDataStore();

const { sajuData } = storeToRefs(dataStore);

// 사용자 정보 computed
const userName = computed(() => {
  return sajuData.value?.inputdata?.name || '-';
});

const nationality = computed(() => {
  const bornArea = sajuData.value?.transdata?.bornArea;
  if (bornArea?.county && bornArea?.city) {
    return { country: bornArea.county, city: bornArea.city };
  }
  return { country: '대한민국', city: '' };
});

const yearSign = computed(() => {
  return 사주정보.value?.현재년운 || '-';
});

const age = computed(() => {
  const korAge = 사주정보.value.나이 || '';
  const realAge = 사주정보.value.만나이 || '';
  if (korAge) {
    return realAge ? `${korAge}세(만${realAge}세)` : `${korAge}세`;
  }
  return '-';
});

const birthDate = computed(() => {
  return 사주정보.value?.생년월일?.split(' ')[0] || '-';
});

const birthTime = computed(() => {
  return 사주정보.value?.생년월일?.split(' ')[1] || '';
});

const birthCode = computed(() => {
  const raw = 사주정보.value?.사주 || '-';
  // "무오계사병술경인(戊午癸巳丙戌庚寅)" → "무오 계사 병술 경인(戊午 癸巳 丙戌 庚寅)"
  const addSpaces = (str) => str.match(/.{2}/g)?.join(' ') || str;
  const match = raw.match(/^(.+?)\((.+?)\)$/);
  if (match) return `${addSpaces(match[1])}(${addSpaces(match[2])})`;
  return raw;
});

const dayCode = computed(() => {
  const 일주 = 사주정보.value?.일주 || '';
  if (!일주) return '-';
  const raw = 사주정보.value?.사주 || '';
  const match = raw.match(/^(.+?)\((.+?)\)$/);
  if (match) {
    const idx = match[1].indexOf(일주);
    if (idx >= 0) return `${일주}(${match[2].substring(idx, idx + 일주.length)})`;
  }
  return 일주;
});

const majorFlow = computed(() => {
  return 사주정보.value?.현재대운 || '-';
});

const cycleNumber = computed(() => {
  // 대운수
  const luckNum = sajuData.value?.transdata?.lucknum;
  if (luckNum) {
    return `${luckNum}(대운수)`;
  }
  return '-';
});

// 패스포트 캐릭터 이미지 동적 import
const passportImages = import.meta.glob('@manseImages/yearly-fortune/passport/*.png', {
  eager: true,
  import: 'default',
});

const passportCharacterImage = computed(() => {
  const 일주 = 사주정보.value?.일주 || '';
  const 현재년운 = 사주정보.value?.현재년운 || '';
  const gender = sajuData.value?.inputdata?.gender;

  // 일주가 없으면 현재년운 앞 두 글자 사용
  const ganji = 일주 || 현재년운.slice(0, 2);
  if (!ganji) return null;

  const { five, animal } = getGanjiCoverData(ganji);
  if (!five || !animal) return null;

  // gender: 1=남자(m), 2=여자(f)
  const genderCode = gender === 1 ? 'm' : 'f';
  const fileName = `img_passport_${animal}_${five}_${genderCode}.png`;

  // glob 결과에서 매칭되는 이미지 찾기
  const key = Object.keys(passportImages).find((k) => k.endsWith(fileName));
  return key ? passportImages[key] : null;
});

// 천간 → 영문 매핑
const skyToEng = {
  갑: 'gap',
  甲: 'gap',
  을: 'eul',
  乙: 'eul',
  병: 'byeong',
  丙: 'byeong',
  정: 'jeong',
  丁: 'jeong',
  무: 'mu',
  戊: 'mu',
  기: 'gi',
  己: 'gi',
  경: 'gyeong',
  庚: 'gyeong',
  신: 'sin',
  辛: 'sin',
  임: 'im',
  壬: 'im',
  계: 'gye',
  癸: 'gye',
};

// 패스포트 stamp 이미지 (천간 기준)
const passportStampImage = computed(() => {
  const 일주 = 사주정보.value?.일주 || '';
  const 현재년운 = 사주정보.value?.현재년운 || '';

  // 일주가 없으면 현재년운 앞 두 글자 사용
  const ganji = 일주 || 현재년운.slice(0, 2);
  if (!ganji) return null;

  const sky = ganji[0]; // 천간 (첫 글자)
  const skyEng = skyToEng[sky];
  if (!skyEng) return null;

  const fileName = `img_passport_stamp_${skyEng}.png`;
  const key = Object.keys(passportImages).find((k) => k.endsWith(fileName));
  return key ? passportImages[key] : null;
});
</script>

<template>
  <VRow class="full-height over-hidden">
    <!-- 왼쪽 컬럼: Life Passport -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right passport-card">
        <div class="passport-wrapper" :class="{ mobile: isMobile }">
          <!-- 타이틀 -->
          <div class="passport-title">
            <div class="passport-title__line"></div>
            <span class="passport-title__text">2026 Life Passport</span>
            <div class="passport-title__line"></div>
          </div>

          <!-- 메인 + 코드 래퍼 -->
          <div class="passport-content" :class="{ mobile: isMobile }">
            <!-- 메인 정보 영역 -->
            <div class="passport-main" :class="{ mobile: isMobile }">
              <!-- 캐릭터 이미지 -->
              <div class="passport-character" :class="{ mobile: isMobile }">
                <img
                  v-if="passportCharacterImage"
                  :src="passportCharacterImage"
                  alt="passport character"
                  class="passport-character__img"
                />
                <div v-else class="passport-character__img passport-character__img--default"></div>
              </div>

              <!-- 정보 그리드 -->
              <div class="passport-info" :class="{ mobile: isMobile }">
                <div class="passport-info__row">
                  <div class="passport-info__group">
                    <span class="passport-info__label">NAME</span>
                    <span class="passport-info__value name">{{ userName }}</span>
                  </div>
                  <div class="passport-info__group">
                    <span class="passport-info__label">Nationality</span>
                    <span class="passport-info__value">
                      {{ nationality.country }}
                      <!-- <span>{{ nationality.country }} / {{ nationality.city }}</span> -->
                      <span v-if="nationality.city">/ {{ nationality.city }}</span>
                    </span>
                  </div>
                </div>

                <div class="passport-info__row">
                  <div class="passport-info__group">
                    <span class="passport-info__label">Year Sign</span>
                    <span class="passport-info__value">{{ yearSign }}</span>
                  </div>
                  <div class="passport-info__group">
                    <span class="passport-info__label">Age(in 2026)</span>
                    <span class="passport-info__value">{{ age }}</span>
                  </div>
                </div>

                <div class="passport-info__row single">
                  <div class="passport-info__group full">
                    <span class="passport-info__label">Date / Time of Birth</span>
                    <div class="passport-info__value-column">
                      <span class="passport-info__value">Date of Birth: {{ birthDate }}</span>
                      <span class="passport-info__value">Time of Birth: {{ birthTime }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 코드 정보 영역 -->
            <div class="passport-codes" :class="{ mobile: isMobile }">
              <div class="passport-codes__item">
                <span class="passport-codes__label">BIRTH-CODE(시/일/월/년)</span>
                <span class="passport-codes__value">{{ birthCode }}</span>
              </div>
              <div class="passport-codes__item">
                <span class="passport-codes__label">DAY-CODE</span>
                <span class="passport-codes__value">{{ dayCode }}</span>
              </div>
              <div class="passport-codes__item">
                <span class="passport-codes__label">MAJOR-FLOW</span>
                <span class="passport-codes__value">{{ majorFlow }}</span>
              </div>
              <div class="passport-codes__item">
                <span class="passport-codes__label">CYCLE NUMBER</span>
                <span class="passport-codes__value">{{ cycleNumber }}</span>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>

    <!-- 오른쪽 컬럼: 서문 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height passport-card">
        <div class="passport-intro" :class="{ mobile: isMobile }">
          <!-- 서문 타이틀 -->
          <div class="passport-intro__title">2026년, 한해는 하나의 여행과도 같습니다.</div>

          <!-- 서문 내용 -->
          <div class="passport-intro__content">
            <p class="passport-intro__text">
              누군가에게는 깊이 깨닫는 여행이 되고,<br />
              누군가에게는 경험이 풍부한 여정이 됩니다.
            </p>
            <p class="passport-intro__text">
              이 여정은 같은 출발선에서 시작되지만,<br />
              준비한만큼 다른 풍경을 만나기도 합니다.
            </p>
            <p class="passport-intro__text">
              이렇게 과정과 결론은 사람마다 다르겠지만<br />
              누구에게나 시간은 동일하게 주어집니다.
            </p>
            <p class="passport-intro__text">
              2026년의 시간이<br />
              행복한 여행의 시간으로 채워질 수 있기를 소망합니다.
            </p>
          </div>

          <!-- 인용문과 도장 -->
          <div class="passport-intro__footer">
            <div class="passport-intro__quote">"모든 여정에는 나에게 맞는 속도가 있습니다."</div>
            <img
              v-if="passportStampImage"
              :src="passportStampImage"
              alt="passport stamp"
              class="passport-intro__stamp"
            />
            <div v-else class="passport-intro__stamp passport-intro__stamp--default"></div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
@import '@/assets/nangman_style/_analysis_main_color.scss';

.passport-card {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('@manseImages/yearly-fortune/img_year_guide_bg.svg');
    pointer-events: none;
  }
}

.passport-wrapper {
  width: 100%;
  max-width: 600px;
  padding: clamp(20px, 4vh, 40px) clamp(16px, 2vw, 40px);
  padding-bottom: 130px;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: clamp(16px, 3vh, 32px);
  height: 100%;
  position: relative;
  z-index: 1;
  overflow-y: auto;

  &.mobile {
    max-width: 480px;
    padding: 24px 20px;
    gap: 24px;
  }
}

// 타이틀
.passport-title {
  margin-bottom: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(12px, 2vw, 16px);
  width: 100%;

  &__line {
    flex: 1;
    max-width: 83px;
    height: 2px;
    background: #303c67;
  }

  &__text {
    color: #303c67;
    font-size: clamp(20px, 2.5vw, 25px);
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
  }

  @media (max-width: 768px) {
    margin-bottom: 32px;
  }
}

.passport-content {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 32px;
}

// 메인 정보 영역
.passport-main {
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: clamp(12px, 2vw, 24px);
  align-items: flex-start;

  &.mobile {
    margin-bottom: clamp(18px, 2vh, 36px);
    gap: 20px;
  }
}

// 캐릭터
.passport-character {
  flex-shrink: 0;

  &__img {
    width: clamp(120px, 15vw, 184px);
    height: auto;
    aspect-ratio: 184/229;
    border-radius: 8px;
    object-fit: contain;

    // fallback 이미지 (기본 프로필)
    &--default {
      background: url('@manseImages/yearly-fortune/img_year_passport_profile.png') center center
        no-repeat;
      background-size: contain;
    }
  }

  &.mobile {
    .passport-character__img {
      @media (max-width: 768px) {
        margin-right: 12px;
        width: 140px;
      }

      @media (max-width: 480px) {
        margin-right: 0px;
        width: 120px;
      }
    }
  }
}

// 정보 그리드
.passport-info {
  max-width: 300px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;

  &.mobile {
    width: 100%;
  }

  &__row {
    display: flex;
    flex-wrap: wrap;
    gap: clamp(12px, 0.5vw, 24px);

    &.single {
      flex-direction: column;
    }
  }

  &__group {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 120px;

    &.full {
      width: 100%;
    }

    @media (max-width: 480px) {
      min-width: 90px;
      gap: 2px;
    }
  }

  &__label {
    color: var(--Other-, #4e80d6);
    font-family: Pretendard;
    font-size: clamp(13px, 1.2vw, 15px);
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
  }

  &__value {
    max-width: 270px;
    display: flex;
    flex-wrap: wrap;
    color: var(--Gray-800, #333);
    font-family: Pretendard;
    font-size: clamp(14px, 1.3vw, 16px);
    font-style: normal;
    font-weight: 500;
    line-height: 24px;

    @media (max-width: 768px) {
      font-size: 15px;
      line-height: 20px;
    }

    &.name {
      // display: block;
      // word-break: break-all;
    }
  }

  &__value-column {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
}

// 코드 정보
.passport-codes {
  margin-top: 40px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: clamp(8px, 1.5vh, 12px);

  &.mobile {
    margin-top: 0;
    gap: 20px;
    padding-top: 12px;
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: 4px;

    @media (max-width: 480px) {
      gap: 2px;
    }
  }

  &__label {
    color: var(--Other-, #4e80d6);
    font-family: Pretendard;
    font-size: clamp(13px, 1.2vw, 15px);
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
  }

  &__value {
    color: var(--Gray-800, #333);
    font-family: Pretendard;
    font-size: clamp(14px, 1.3vw, 16px);
    font-style: normal;
    font-weight: 500;
    line-height: 24px;

    @media (max-width: 768px) {
      font-size: 15px;
      line-height: 20px;
    }
  }
}

// 오른쪽 서문 영역
.passport-intro {
  position: relative;
  z-index: 1;
  overflow-y: auto;
  padding: clamp(24px, 5vh, 40px) clamp(20px, 4vw, 32px) 130px;
  width: 100%;
  min-height: calc(100vh - 130px);
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: clamp(20px, 4vh, 32px);
  overflow-x: hidden;

  &.mobile {
    padding: 24px 16px 40px;
    gap: 24px;
  }

  &__title {
    color: #333;
    font-family: Pretendard;
    font-size: clamp(16px, 1.5vw, 18px);
    font-weight: 700;
    line-height: 1.5;
    text-align: center;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: clamp(12px, 2.5vh, 20px);
  }

  &__text {
    color: #333;
    font-family: Pretendard;
    font-size: clamp(14px, 1.3vw, 16px);
    font-weight: 500;
    line-height: 1.5;
    text-align: center;
    margin: 0;

    @media (max-width: 768px) {
      font-size: 15px;
    }
  }

  &__footer {
    position: relative;
    margin-top: clamp(40px, 8vh, 100px);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: clamp(12px, 2.5vh, 20px);
  }

  &__quote {
    color: #303c67;
    text-align: center;
    font-family: Pretendard;
    font-size: clamp(16px, 1.5vw, 18px);
    font-style: normal;
    font-weight: 700;
    line-height: 130%;
  }

  &__stamp {
    position: absolute;
    right: 0;
    bottom: 0px;
    transform: translate(50%, 50%);
    width: clamp(180px, 15vw, 220px);
    height: clamp(180px, 15vw, 220px);
    opacity: 0.8;
    object-fit: contain;

    // fallback 이미지
    &--default {
      background: url('@manseImages/yearly-fortune/passport/img_passport_stamp_default.png') center
        center no-repeat;
      background-size: contain;
    }
  }
}

// 중앙 점선 (여권 스타일)
.passport-card.right::after {
  content: '';
  position: absolute;
  top: 40px;
  bottom: 40px;
  right: 0;
  width: 1px;
  background: repeating-linear-gradient(
    to bottom,
    #d0d7e8 0px,
    #d0d7e8 8px,
    transparent 8px,
    transparent 16px
  );
}
</style>
